import { TaskItem } from '../models/task.model';
import { AllocationOrigin, BulkMoveCapture } from '../models/allocation-event-log.model';

// ML action-log capture v2: provenance stamps for pending changes.
//
// The interaction paths (key-staff auto-allocation, single drag, whole-row
// bulk move, care-plan dialog) call these to record WHAT set the carer / time
// that is pending, in which ORDER, and WHEN. They only assign the optional
// log_* fields of a TaskItem, which nothing but the action log reads
// (ChangeManagementService.buildLogEvent), and they swallow every error:
// capture must never be able to affect scheduling or a publish.

// Session-wide counter behind change_seq. It lives here, not on the TaskItem,
// because a day reload rebuilds every item; the counter keeps counting until
// the page itself is reloaded.
let changeSeq = 0;

// "This service was just mutated."
const touch = (task: Partial<TaskItem>): void => {
  task.log_change_seq = ++changeSeq;
  task.log_changed_at = new Date().toISOString();
};

// String() coercion matters: after a drag the dhtmlx scheduler holds
// carer_string as a NUMBER (the row key), even when the row did not change.
const carerKey = (carerString: unknown): string => String(carerString ?? '');

// Same minute: the resolution the drag handler itself compares at
// (HelperService.areDatesEqual). The scheduler holds Date objects, loaded
// items hold strings; two unparseable values count as equal.
const sameMinute = (a: unknown, b: unknown): boolean => {
  const minuteA = Math.floor(new Date(a as string).getTime() / 60000);
  const minuteB = Math.floor(new Date(b as string).getTime() / 60000);
  return minuteA === minuteB || (isNaN(minuteA) && isNaN(minuteB));
};

// Auto-allocation, the bulk move and the care-plan dialog: `origin` has just
// set the carer that is now pending. Last writer wins.
export function stampAllocationOrigin(task: Partial<TaskItem>, origin: AllocationOrigin, bulk?: BulkMoveCapture): void {
  try {
    task.log_allocation_origin = origin;
    task.log_bulk = origin === 'bulk_row' ? bulk : undefined;
    touch(task);
  } catch { /* capture is best-effort */ }
}

// The pending allocation was withdrawn (auto-allocation switched off).
export function clearAllocationOrigin(task: Partial<TaskItem>): void {
  try {
    task.log_allocation_origin = undefined;
    task.log_bulk = undefined;
    touch(task);
  } catch { /* capture is best-effort */ }
}

// The single-drag handler (onBeforeEventChanged), once it has recomputed the
// pending flags. `original` is the scheduler's pre-drag copy of the event, so
// a dimension is stamped only if THIS drag changed it: retiming an
// auto-allocated service leaves its allocation origin alone. A dimension that
// is no longer pending (dragged back to its loaded carer / time) loses its
// origin.
export function stampDrag(ev: TaskItem, original: Partial<TaskItem>): void {
  try {
    const carerChanged = carerKey(ev.carer_string) !== carerKey(original.carer_string);
    const timeChanged = !sameMinute(ev.start_date, original.start_date);

    if (!ev.pending_allocation_change) {
      ev.log_allocation_origin = undefined;
      ev.log_bulk = undefined;
    } else if (carerChanged) {
      ev.log_allocation_origin = 'drag';
      ev.log_bulk = undefined;
    }

    if (!ev.pending_timing_change) {
      ev.log_timing_origin = undefined;
    } else if (timeChanged) {
      ev.log_timing_origin = 'drag';
    }

    if (carerChanged || timeChanged) {
      touch(ev);
    }
  } catch { /* capture is best-effort */ }
}

// One per bulk gesture, shared by every service the gesture moves. The caller
// sets `size` once the gesture has finished.
export function newBulkMove(sourceId: unknown, targetId: unknown): BulkMoveCapture {
  const bulk: BulkMoveCapture = { id: '', size: 0, source_id: '', target_id: '' };
  try {
    bulk.source_id = String(sourceId ?? '');
    bulk.target_id = String(targetId ?? '');
    const c: any = (typeof crypto !== 'undefined') ? crypto : null;
    bulk.id = c?.randomUUID
      ? c.randomUUID()
      : `bulk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  } catch { /* capture is best-effort */ }
  return bulk;
}
